import React, {
  useState, 
  useEffect, 
  useContext,
  useCallback
} from "react";
import { 
  Box,
  Table,
  TableBody,
  TableContainer,
  Paper,
} from '@mui/material';
import { TABS } from "../helpers/Tabs";
import { useBebackApi } from '../hooks/useBebackApi';
import { stillSendSms } from "../helpers/SMS";
import { useMRTableCols } from "../hooks/useMRTableCols";
import { useMRTable } from "../hooks/useMRTable";
import { useMRTableModalsContext } from "../context/MRTableModalsContext";
import StoreContext from "../context/StoreContext";
import WorkOrderContext from "../context/WorkOrderContext";
import SmsContext from "../context/SmsContext";
import UserContext from '../context/UserContext';
import FeedbackContext from "../context/FeedbackContext";
import TablePagination from '@mui/material/TablePagination';
import MRTableSearchToolbar from './MRTableSearchToolbar';
import MRTableHeaders from './MRTableHeaders';
import MRTableRow from './MRTableRow';
import MRTableModals from "./MRTableModals";
import MRTableNoRecords from "./MRTableNoRecords";
import PropTypes from 'prop-types';

const MRTable = ({index, currentWorkOrders, day, searchedValue, setSearchedValue, setIndex, setInfoPopup, setOpenInfoPopup}) => {
  const [currentWorkOrder, setCurrentWorkOrder] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { storeId, globalBeback } = useContext(StoreContext);
  const { setSendingSms, switchToUpdate, setSwitchToUpdate, setMessageType} = useContext(SmsContext);
  const { fetchWorkOrders, canceledLyftWorkOrder } = useContext(WorkOrderContext);
  const { username } = useContext(UserContext);
  const { postMarshalSMS, updatePhone: updatePhoneQuery} = useBebackApi()
  const { setIsLoading } = useContext(FeedbackContext);
  const { 
    togglePhoneEdit, 
    onOpenNextApptCalendar, 
    onOpenLyftDialog,
    onOpenCanceledRideDialog
  } = useMRTableModalsContext();
  const { 
    requestSearch,
    emptySearch,
    order,
    orderBy,
    handleRequestSort,
    page,
    toggleRow,
    openRows,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    visibleRows,
    setVisibleRows,
    rowsCount,
    updateSmsData
  } = useMRTable(currentWorkOrders, searchedValue, setSearchedValue, day, 'WorkOrder')

  useEffect(() => {
    if(switchToUpdate.smsType){
      updateSmsData(switchToUpdate)
      setSwitchToUpdate({})
    }
  }, [switchToUpdate, setSwitchToUpdate]);

  useEffect(() => {
    if(currentWorkOrder?.WO) {
      const WOIndex = currentWorkOrders.findIndex(wo => wo.WO === currentWorkOrder.WO);

      if (WOIndex !== -1) {
        let WOToUpdate = { ...currentWorkOrders[WOIndex] };
        setCurrentWorkOrder(WOToUpdate)
      }
    }
  },[currentWorkOrders])

  useEffect(() => {
    if(canceledLyftWorkOrder) {
      onOpenCanceledRideDialog();
    }
  },[canceledLyftWorkOrder])

  const redirectToOtherTab = () => {
    setIndex((index) => (index+1)%TABS.length);
  }

  const notifyCustomer = async (workOrder, type, contactInfo = {}) => {
    setMessageType(type)
    const params = {
      phoneNumber: workOrder.Phone,
      sentCount: workOrder.MarshalSmsCount,
      siteNo: workOrder.SiteNo,
      messageType: type,
      woNumber: workOrder.WO,
      username,
      ...contactInfo
    };

    try {
      let sendingMessage = false;
      setSendingSms((prev) => { 
        return [
          ...prev, 
          {
            wo: workOrder.WO,
            messageType: type
          }
        ]
      })
      const responsePostMarshal = await postMarshalSMS({...params});
      if(!responsePostMarshal) {
        sendingMessage = stillSendSms(type, workOrder, globalBeback);
      }
      
      setTimeout(async () => {
        if(sendingMessage) {
          setSendingSms((prev) => { return prev.filter(wo => !(wo.wo === workOrder.WO && wo.messageType === type))})
          await fetchWorkOrders(storeId);
        }
      }, 60.0 * 1000); 
      
    } catch (error) {
      await fetchWorkOrders(storeId);
      console.log(error);
    } 
  };
  
  /* Modals */
  const openPhoneEdit = (workOrder, workOrderIndex) => {
    setCurrentWorkOrder(workOrder);
    setSelectedIndex(workOrderIndex);
    togglePhoneEdit();
  }
  
  const handleLyftDialog = useCallback((workOrder) => {
    setCurrentWorkOrder(workOrder);
    onOpenLyftDialog(workOrder)
  },[])

  const handleNextApptCalendar = (rowData) => {
    setCurrentWorkOrder(rowData);
    onOpenNextApptCalendar(rowData);
  }

  /* Update Phone */
  const updatePhone = async (newPhone) => {
    const params = { 
      wo: currentWorkOrder.WO, 
      newPhone,
      siteNo: currentWorkOrder.SiteNo 
    };

    try {
      visibleRows[selectedIndex].Phone = newPhone;
      setVisibleRows(visibleRows);
      const result = await updatePhoneQuery({...params});
      if(result === undefined) {
        return new Error('Service unavailable. Please try again later.');
      }
      togglePhoneEdit();
      setIsLoading(false);
    } catch (error) {
      await fetchWorkOrders(storeId);
      console.log(error)
      setIsLoading(false);
    }
  }

  const MRcols = useMRTableCols({
    handleLyftDialog,
    handleNextApptCalendar,
    setInfoPopup,
    setOpenInfoPopup,
    notifyCustomer,
    openPhoneEdit,
    toggleRow,
    openRows
  })

  return (
    <div>
      <MRTableModals
        updatePhone={updatePhone}
        currentWorkOrder={currentWorkOrder}
        setCurrentWorkOrder={setCurrentWorkOrder}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <MRTableSearchToolbar
          value={searchedValue}
          onChange={search => requestSearch(search.target.value)}
          clearSearch={emptySearch}
          placeholder={`Search ${TABS[index].title}`}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <MRTableHeaders
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            MRcols={MRcols}
          />
          <TableBody>
            {
              visibleRows.length > 0
              ? 
                visibleRows.map((row, index) => (
                  <MRTableRow 
                    key={index} 
                    row={row} 
                    index={index}
                    MRcols={MRcols}
                    openRows={openRows}
                  />
                ))
              : 
              <MRTableNoRecords
                tabIndex={index}
                searchedValue={searchedValue}
                redirectToOtherTab={redirectToOtherTab}
                emptySearch={emptySearch}
              />
            }
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 35, 50]}
        component="div"
        count={rowsCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showFirstButton
        showLastButton
        className='table-pagination'
      />
    </div>
  );
};

MRTable.propTypes = {
  index: PropTypes.number,
  currentWorkOrders: PropTypes.array,
  day: PropTypes.string,
  searchedValue: PropTypes.string,
  setSearchedValue: PropTypes.func,
  setIndex: PropTypes.func,
  setInfoPopup: PropTypes.func,
  setOpenInfoPopup: PropTypes.func
}

export default MRTable;